'use client'

import { Avatar, Icon, IconComponent } from '@graftcode/design-system'
import * as Dialog from '@radix-ui/react-dialog'

import { MOBILE_MENU_TEST_IDS } from '@common/common-test-ids.const'
import { getInitials } from '@common/components/Sidebar/Sidebar.helpers'
import { getSafeImageUrl } from '@common/helpers'

import { useMobileMenuContext } from '../../context'

import styles from '../../MobileMenu.module.scss'

export function ProjectPickerDrawer() {
  const {
    currentProject,
    isProjectPickerOpen,
    mobileMenuProjects,
    onInviteProjectClick,
    onOpenProjectMembers,
    onOpenProjectSettings,
    onProjectClick,
    setIsProjectPickerOpen,
  } = useMobileMenuContext()

  const currentProjectId = currentProject?.projectId

  const handleClose = () => setIsProjectPickerOpen(false)

  const handleProjectClick = (projectId: string) => {
    onProjectClick?.(projectId)
    handleClose()
  }

  const handleSettingsClick = () => {
    onOpenProjectSettings?.()
    handleClose()
  }

  const handleMembersClick = () => {
    onOpenProjectMembers?.()
    handleClose()
  }

  const handleInviteClick = () => {
    onInviteProjectClick?.()
    handleClose()
  }

  const settingsEnabled = Boolean(onOpenProjectSettings)
  const projectMembersEnabled = Boolean(onOpenProjectMembers)
  const inviteEnabled = Boolean(onInviteProjectClick)

  return (
    <Dialog.Root open={isProjectPickerOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.appPickerOverlay} />
        <Dialog.Content className={styles.appPickerDrawer} data-testid={MOBILE_MENU_TEST_IDS.projectPickerDrawer}>
          <div className={styles.appPickerHeader}>
            <div className={styles.appPickerDragHandle} />
            <Dialog.Title className={styles.appPickerTitle}>Select project</Dialog.Title>
            <div className={styles.appPickerDivider} />
          </div>

          <div className={styles.appPickerContent}>
            <button
              type="button"
              className={`${styles.appPickerItem} ${!settingsEnabled ? styles.disabled : ''}`}
              disabled={!settingsEnabled}
              onClick={handleSettingsClick}
            >
              <IconComponent icon={Icon.SettingsRegular} size={24} className={styles.appPickerItemIcon} />
              <span className={styles.appPickerItemLabel}>Settings</span>
            </button>
            <button
              type="button"
              className={`${styles.appPickerItem} ${!projectMembersEnabled ? styles.disabled : ''}`}
              disabled={!projectMembersEnabled}
              onClick={handleMembersClick}
            >
              <IconComponent icon={Icon.PeopleRegular} size={24} className={styles.appPickerItemIcon} />
              <span className={styles.appPickerItemLabel}>Project members</span>
            </button>
            <button
              type="button"
              className={`${styles.appPickerItem} ${!inviteEnabled ? styles.disabled : ''}`}
              disabled={!inviteEnabled}
              onClick={handleInviteClick}
            >
              <IconComponent icon={Icon.PeopleAddRegular} size={24} className={styles.appPickerItemIcon} />
              <span className={styles.appPickerItemLabel}>Invite people</span>
            </button>

            <div className={styles.accountDrawerSeparator} />
            <div className={styles.pickerSectionLabel}>Switch project</div>

            {mobileMenuProjects.length === 0 ? (
              <div className={styles.pickerEmptyMessage}>No projects available</div>
            ) : (
              mobileMenuProjects.map((project) => {
                const isSelected = project.projectId === currentProjectId
                const initials = getInitials(project.name)

                return (
                  <button
                    key={project.projectId}
                    className={`${styles.appPickerItem} ${isSelected ? styles.selected : ''}`}
                    onClick={() => handleProjectClick(project.projectId)}
                  >
                    <Avatar size={24} src={getSafeImageUrl(project.logoUrl)} fallback={initials} />
                    <span className={styles.appPickerItemLabel}>{project.name}</span>
                    {isSelected ? (
                      <IconComponent icon={Icon.CheckmarkRegular} size={24} className={styles.appPickerCheckmark} />
                    ) : null}
                  </button>
                )
              })
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
